/**
 * NotFoundPage - 404 페이지
 *
 * 잘못된 URL 진입 시 silent home fallback 차단 (#381).
 * 사용자에게 명확한 404 안내와 홈으로 돌아가기 동선을 제공합니다.
 *
 * @see Issue #381 - AppRoutes catchall '*' silent 404 fallback 차단
 */

import { useNavigate } from 'react-router-dom';
import { Compass, Home } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';

const texts = {
  ko: {
    code: '404',
    title: '페이지를 찾을 수 없습니다',
    description: '요청하신 페이지가 존재하지 않거나 이동되었습니다. 주소를 다시 확인해주세요.',
    goHome: '홈으로 돌아가기',
  },
  en: {
    code: '404',
    title: 'Page not found',
    description: 'The page you are looking for does not exist or has been moved. Please check the URL.',
    goHome: 'Go to Home',
  },
  es: {
    code: '404',
    title: 'Página no encontrada',
    description: 'La página que busca no existe o ha sido movida. Verifique la URL.',
    goHome: 'Volver al inicio',
  },
} as const;

export function NotFoundPage() {
  const navigate = useNavigate();
  const { language } = useApp();
  const t = texts[language] || texts.es;

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  return (
    <main
      className="flex flex-col items-center justify-center min-h-[70vh] gap-6 px-6"
      role="main"
    >
      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
        <Compass className="h-10 w-10 text-muted-foreground" />
      </div>

      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-foreground">{t.code}</h1>
        <h2 className="text-xl font-semibold text-foreground">{t.title}</h2>
        <p className="text-sm text-muted-foreground max-w-xs">{t.description}</p>
      </div>

      <button
        type="button"
        onClick={handleGoHome}
        className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        <Home className="h-4 w-4" />
        {t.goHome}
      </button>
    </main>
  );
}

export default NotFoundPage;
